import { FC, useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import { Typography } from "@mui/material";
import { Chart, Point } from "highcharts";
import { Cube } from "../../../charts/multi-type-chart/components/common/Cube";
import { chartPieHighlightFunctions } from "../utils/helpers/chartPieHighlightFunctions";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { money } from "@/utils/numeral/money";
import { getXStyles } from "@/storybook/c8r-highcharts/common/components/legends/LegendItem";

interface PieChartLegendItemProps {
  name: string;
  value: number;
  bgcolor?: string;
  chartApi: Chart | null;
}

export const PieChartLegendItem: FC<PieChartLegendItemProps> = ({
  name,
  value,
  bgcolor,
  chartApi,
}) => {
  const pieChartData = chartApi?.series?.at(0)?.data;

  const currentSlice = useMemo(
    () => pieChartData?.find((slice) => slice.name === name),
    [pieChartData, name],
  );
  const { visible, color } = currentSlice ?? {};

  const onMouseEnter = useCallback(() => {
    chartPieHighlightFunctions.onHoverIn(chartApi, name);
  }, [chartApi, name]);

  const onMouseLeave = useCallback(() => {
    chartPieHighlightFunctions.onHoverOut(chartApi);
  }, [chartApi]);

  const hideCurrentLegend = useCallback(() => {
    if (!chartApi) return;

    try {
      chartApi.series[0]?.data.forEach((point) => {
        if (point.name === name) {
          hidePieLegend(point, !!visible);
        }
      });
    } catch (e) {
      console.error(`LegendItem toggleSlice error: ${e}`);
    }
  }, [chartApi, name, visible]);

  const hideAllLegendBesidesCurrent = useCallback(() => {
    if (!chartApi) return;
    const allTurnedOffBesidesCurrent = chartApi.series[0]?.data
      .filter(({ name: legendName }) => legendName !== name)
      .every(({ visible }) => !visible);

    try {
      chartApi.series[0]?.data.forEach((point) => {
        if (point.name === name) {
          hidePieLegend(point, false);
        }

        if (point.name !== name) {
          hidePieLegend(point, !allTurnedOffBesidesCurrent);
        }
      });
    } catch (e) {
      console.error(`LegendItem toggleSlice error: ${e}`);
    }
  }, [chartApi, name]);

  const xStyles = useMemo(
    () => getXStyles(visible ? "white" : `${color}`),
    [visible, color],
  );

  return (
    <Stack
      p={0.5}
      direction="row"
      sx={stackStyles}
      bgcolor={bgcolor}
      alignItems="center"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Cube
        width={12}
        height={12}
        onClick={hideCurrentLegend}
        color={visible ? `${color}` : "transparent"}
        border={visible ? undefined : `1px solid ${color}`}
        sx={xStyles}
      />

      <Stack
        flex={1}
        spacing={2}
        direction="row"
        justifyContent="space-between"
        sx={ellipsisStyles}
        onClick={hideAllLegendBesidesCurrent}
      >
        <TypographyWithTooltip
          title={name}
          variant="body2"
          sx={visible ? undefined : { textDecoration: "line-through" }}
        />

        <Typography variant="body2" fontWeight="bold">
          {money(value)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ellipsisStyles = {
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
};

const stackStyles = {
  cursor: "pointer",
  "&:hover": {
    bgcolor: "grey.200",
  },
  ...ellipsisStyles,
};

const hidePieLegend = (point: Point, hide: boolean) => {
  point.setVisible(!hide);

  const label = document.querySelector(
    `[data-point='${point.name}']`,
  ) as HTMLElement;

  if (label) {
    label.style.opacity = hide ? "0.5" : "1";
  }
};
